import { useEffect } from 'react';

import { useRouter } from 'next/router';

import Tracker from '~/src/common/services/Tracker';
import { getCommonProductsEventProperties } from '~/src/common/utils/product';
import { useSearchItemsInfinite } from '~/src/queries/api-ecom/generated/api-ecom';
import { isProduct } from '~/src/typings/products/guards';

import { BACKEND_TO_AMPLITUDE, SEARCH_QS } from './constants';
import { Source, TabName, Type } from './types';

/** Extrait les params de recherche du routeur */
export const useSearchParams = () => {
  const { query } = useRouter();
  const term = String(query[SEARCH_QS.TERM] ?? '');
  const source = String(query[SEARCH_QS.SOURCE] ?? '');
  const searchID = String(query[SEARCH_QS.SEARCHID] ?? '');
  return { term, source, searchID };
};

/** Recherche pour un type de résultats en particulier */
const useSearchResultsType = (type: Type, text: string, source?: Source, tabName?: TabName) => {
  const { searchID } = useSearchParams();
  // Récupération des données correspondant au type, via une liste infinie
  const results = useSearchItemsInfinite(
    { type, text },
    { query: { getNextPageParam: page => page.next, staleTime: Infinity } },
  );

  // Liste des elements
  const { data } = results;
  const count = data?.pages[0]?.count;
  const items = data?.pages.flatMap(p => p.items);

  // Booleen utilitaires
  const { isError, isFetching, isFetched } = results;
  const isLoaded = (isFetched && !!data) || isError;

  // Gestion du chargement infini
  const { hasNextPage, fetchNextPage } = results;
  const loadMore = () => void fetchNextPage();
  const hasMore = hasNextPage === true;

  // Amplitude
  useEffect(() => {
    // Derniere page et son index
    const index = data?.pages.length;
    const lastPage = index ? data.pages[index - 1] : null;
    const resultsType = BACKEND_TO_AMPLITUDE[type];
    // Envoi si sur bon onglet, et si toutes les données sont présentes
    if (resultsType !== tabName || index == null || lastPage == null) return;

    Tracker.sendEvent('view search results', {
      'is page viewed': true,
      'keywords': text,
      'search type': source,
      'results type': resultsType,
      'number of search results': lastPage.count,
      'algorithm': lastPage.algoVersion,
      'page index': index,
      ...(searchID ? { 'search ID': searchID } : null),
      ...(type === 'PRODUCT' && getCommonProductsEventProperties(lastPage.items.filter(isProduct))),
    });
  }, [data, text, type, source, tabName, searchID]);

  return { items, count, isError, isLoaded, isFetching, hasMore, loadMore };
};

/** Recherche, incluant produits et recettes */
export const useSearchResults = (tabName: TabName) => {
  // Parametres de recherche
  const { term, source: _source } = useSearchParams();

  // @ts-expect-error - La valeur vient de l'url, typage strict impossible
  // Ici, pas d'utilisation de `as` pour conserver une trace du bricolage
  const source: Source = _source;

  // Récupération des données
  const recipes = useSearchResultsType('RECIPE', term, source, tabName);
  const products = useSearchResultsType('PRODUCT', term, source, tabName);

  // Utilitaires et retour
  const isLoaded = products.isLoaded && recipes.isLoaded;
  const data = { recipes, products };
  return { term, data, isLoaded };
};
